"use client";

import React, { useState } from "react";
import { Button } from "../button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog.js";
import { Card, CardContent, CardHeader, CardTitle } from "../card.js";
import { Badge } from "../badge.js";
import { Calendar, Clock, Users, MapPin, Video, Building } from "lucide-react";
import { useAuth } from "../../hooks/useAuth.js";
import { useToast } from "../../hooks/use-toast.js";
import { AdvancedBookingForm } from "../shared/index.js";
import EmailSelect from "./EmailSelect.js";

interface BookAnyMeetingProps {
  customerName?: string;
  itemLink?: string;
  isMobile?: boolean;
  className?: string;
  availableEmails?: string[];
}

const BookAnyMeeting = ({
  customerName,
  itemLink,
  isMobile = false,
  className,
  availableEmails = [],
}: BookAnyMeetingProps) => {
  const [open, setOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<
    "google" | "microsoft"
  >("google");
  const { getAuthToken } = useAuth();
  const { toast } = useToast();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleEmailChange = (email: string) => {
    setSelectedEmail(email);
  };

  const handlePlatformChange = (platform: "google" | "microsoft") => {
    setSelectedPlatform(platform);
  };

  const handleBookingSubmit = async (data: any) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token");
      }

      // Create event based on selected platform
      const eventData = {
        ...data,
        platform: selectedPlatform,
        selectedEmail,
        customerName,
      };

      // TODO: Implement actual API call based on platform
      console.log("Creating event:", eventData);

      toast({
        title: "Meeting Scheduled",
        description: `Meeting created successfully in ${selectedPlatform === "google" ? "Google" : "Microsoft"} Calendar.`,
      });

      handleClose();
    } catch (error) {
      console.error("Booking failed:", error);
      toast({
        title: "Booking Failed",
        description:
          "There was an error scheduling your meeting. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            onClick={handleOpen}
            size={isMobile ? "sm" : "default"}
            className={className}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Book Any Meeting
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book Any Meeting</DialogTitle>
            <DialogDescription>
              Schedule a meeting with flexible options for calendar platform and
              attendees.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Platform Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Calendar Platform</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedPlatform === "google"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => handlePlatformChange("google")}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-destructive-100 rounded-lg">
                        <Calendar className="h-5 w-5 text-destructive-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Google Calendar</h3>
                        <p className="text-sm text-muted-foreground">
                          Create meetings in Google Calendar
                        </p>
                      </div>
                    </div>
                    {selectedPlatform === "google" && (
                      <Badge className="mt-2" variant="secondary">
                        Selected
                      </Badge>
                    )}
                  </div>

                  <div
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedPlatform === "microsoft"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => handlePlatformChange("microsoft")}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary-100 rounded-lg">
                        <Building className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Microsoft Calendar</h3>
                        <p className="text-sm text-muted-foreground">
                          Create meetings in Microsoft Calendar
                        </p>
                      </div>
                    </div>
                    {selectedPlatform === "microsoft" && (
                      <Badge className="mt-2" variant="secondary">
                        Selected
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Selection */}
            {availableEmails.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Calendar Account</CardTitle>
                </CardHeader>
                <CardContent>
                  <EmailSelect
                    selectedEmail={selectedEmail}
                    handleEmailChange={handleEmailChange}
                    emails={availableEmails}
                    label="Select Calendar Account"
                    placeholder="Choose calendar account..."
                  />
                </CardContent>
              </Card>
            )}

            {/* Meeting Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Meeting Details</CardTitle>
              </CardHeader>
              <CardContent>
                <AdvancedBookingForm
                  onSubmit={handleBookingSubmit}
                  customerName={customerName}
                  platform={selectedPlatform}
                />
              </CardContent>
            </Card>

            {/* Meeting Notes Reminder */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>Meeting Notes Reminder</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">
                    Don't forget to log your meeting notes for the sales meeting
                    that was just scheduled.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    You can log your notes before and after the meeting by
                    visiting the "Log Pre-Meeting or Meeting Notes" button on
                    the page. Set up a reminder for 1 day after the event to log
                    the outcomes of the meeting.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BookAnyMeeting;
